import { inject, Injectable } from '@angular/core';
import { type LayoutContext } from '@bizzdesign/shared/models';
import { LayoutContextService } from '@bizzdesign/shared/services';
import { BehaviorSubject, switchMap } from 'rxjs';

export type SidepanelKeys = 'modelingAssistance' | 'colorLegend';

type ContextSubjects = {
  visibleSidepanelKey$: BehaviorSubject<SidepanelKeys | undefined>;
};

function createContextSubjects(): ContextSubjects {
  return {
    visibleSidepanelKey$: new BehaviorSubject<SidepanelKeys | undefined>(
      undefined,
    ),
  };
}

@Injectable({
  providedIn: 'root',
})
export class SidepanelService {
  private readonly layoutContextService = inject(LayoutContextService);
  private readonly contextMap: Record<LayoutContext, ContextSubjects> = {
    primary: createContextSubjects(),
    overlay: createContextSubjects(),
  };
  readonly visibleSidepanelKey$ = this.layoutContextService.context$.pipe(
    switchMap((context) => this.contextMap[context].visibleSidepanelKey$),
  );

  showSidepanel(key: SidepanelKeys): void {
    this.contextMap[
      this.layoutContextService.context$.value
    ].visibleSidepanelKey$.next(key);
  }

  hideSidepanel(): void {
    this.contextMap[
      this.layoutContextService.context$.value
    ].visibleSidepanelKey$.next(undefined);
  }

  toggleSidepanel(key: SidepanelKeys): void {
    if (
      this.contextMap[this.layoutContextService.context$.value]
        .visibleSidepanelKey$.value === key
    ) {
      this.hideSidepanel();
    } else {
      this.showSidepanel(key);
    }
  }

  isSidepanelVisible(key: SidepanelKeys): boolean {
    return (
      this.contextMap[this.layoutContextService.context$.value]
        .visibleSidepanelKey$.value === key
    );
  }
}
